import * as THREE from 'three';
import { SeededRandom } from '../utils/random.js';
import { CRYSTALS, LAYOUT } from '../utils/constants.js';
import { SurfacePlan } from './SurfacePlan.js';

/* ── Crystal colour palettes per planet type ─────────────────── */

const CRYSTAL_STYLES = {
  rocky:   { color: [0.12, 0.70, 0.55], glow: [0.12, 0.80, 0.50], roughness: 0.08, metalness: 0.30 },
  forest:  { color: [0.35, 0.65, 0.50], glow: [0.35, 0.75, 0.45], roughness: 0.10, metalness: 0.20 },
  ocean:   { color: [0.55, 0.70, 0.55], glow: [0.55, 0.80, 0.50], roughness: 0.05, metalness: 0.35 },
  ice:     { color: [0.56, 0.55, 0.70], glow: [0.56, 0.65, 0.65], roughness: 0.03, metalness: 0.25 },
  lava:    { color: [0.03, 0.90, 0.50], glow: [0.03, 1.00, 0.45], roughness: 0.12, metalness: 0.40 },
  desert:  { color: [0.10, 0.75, 0.60], glow: [0.10, 0.85, 0.55], roughness: 0.06, metalness: 0.35 },
  crystal: { color: [0.80, 0.80, 0.65], glow: [0.80, 0.90, 0.55], roughness: 0.02, metalness: 0.50 },
};

/**
 * Generates glowing crystal deposits on planet surfaces.
 *
 * Crystals are rare, valuable resource nodes that integrate with the
 * landscape. Each deposit is a cluster of glowing pointed shards.
 *
 * All generation is seeded/deterministic for multiplayer.
 */
export class CrystalFormation {
  constructor() {
    /** @type {{ mesh: THREE.Mesh, center: THREE.Vector3, radius: number, health: number, maxHealth: number, baseEmissiveIntensity: number, resourceType: string }[]} */
    this.crystals = [];
    this.group = new THREE.Group();
    this.group.name = 'crystal-formations';
  }

  /* ── public API ────────────────────────────────── */

  /**
   * Place crystal fields over the planet.
   *
   * Most fields glitter at the rim of an anomaly clearing — the glow draws
   * the eye toward the mystery and rewards walking to it. The rest are
   * standalone veins spread through open ground via the shared SurfacePlan.
   *
   * @param {object}      planetData   – planet config
   * @param {number}      planetRadius – SURFACE_RADIUS × size
   * @param {function}    getHeightFn  – (normalizedDir) => surfaceHeight
   * @param {SurfacePlan} [plan]       – shared surface zoning plan
   * @returns {{ group: THREE.Group, crystals: object[] }}
   */
  generate(planetData, planetRadius, getHeightFn, plan = null) {
    this.clear();

    if (planetData.type === 'gas') {
      return { group: this.group, crystals: this.crystals };
    }

    plan = plan || new SurfacePlan(planetData, planetRadius);
    const rng = new SeededRandom(planetData.seed + 55555);

    // Field anchors: ruin-rim fields first, then standalone veins.
    const anchors = [];
    for (const lm of plan.landmarkDirs) {
      if (rng.next() >= CRYSTALS.LANDMARK_CHANCE) continue;
      const rim = plan.offsetFrom(lm, LAYOUT.SITE_CLEARING + CRYSTALS.FIELD_FOOTPRINT * 0.8, rng);
      if (plan.tryReserve(rim, CRYSTALS.FIELD_FOOTPRINT, 'crystal')) anchors.push(rim);
    }
    const veinCount = CRYSTALS.VEINS_MIN +
      Math.floor(rng.next() * (CRYSTALS.VEINS_MAX - CRYSTALS.VEINS_MIN + 1));
    anchors.push(...plan.pickSpreadDirs(veinCount, CRYSTALS.FIELD_FOOTPRINT, 'crystal', rng));

    const minAngle = CRYSTALS.MIN_SPACING / planetRadius;
    let placed = 0;

    for (const anchor of anchors) {
      const perField = CRYSTALS.PER_FIELD_MIN +
        Math.floor(rng.next() * (CRYSTALS.PER_FIELD_MAX - CRYSTALS.PER_FIELD_MIN + 1));
      const fieldDirs = [];

      let fieldPlaced = 0;
      let attempts = 0;
      while (fieldPlaced < perField && attempts < perField * 12) {
        attempts++;
        const dir = plan.sampleNear(anchor, CRYSTALS.FIELD_FOOTPRINT * 0.75, rng);

        let tooClose = false;
        for (const d of fieldDirs) {
          if (dir.angleTo(d) < minAngle) { tooClose = true; break; }
        }
        if (tooClose) continue;
        fieldDirs.push(dir.clone());

        const surfaceHeight = getHeightFn(dir);
        const surfacePoint  = dir.clone().multiplyScalar(surfaceHeight);
        const crystalSeed   = planetData.seed + placed * 271 + 99;
        const crystalRadius = CRYSTALS.MIN_RADIUS + rng.next() * (CRYSTALS.MAX_RADIUS - CRYSTALS.MIN_RADIUS);

        const mesh = this._createCrystalCluster(crystalRadius, crystalSeed, planetData.type);
        mesh.position.copy(surfacePoint);

        // Orient outward
        const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
        mesh.quaternion.copy(quat);
        mesh.name = 'crystal-cluster';
        this.group.add(mesh);

        const resourcePool = CRYSTALS.RESOURCES[planetData.type] || CRYSTALS.RESOURCES.rocky;

        const health = Math.round(CRYSTALS.BASE_HEALTH * (crystalRadius / CRYSTALS.MIN_RADIUS));
        this.crystals.push({
          mesh,
          center: mesh.position.clone(),
          radius: crystalRadius,
          health,
          maxHealth: health,
          baseEmissiveIntensity: mesh.material ? mesh.material.emissiveIntensity || 0.9 : 0.9,
          resourceType: rng.pick(resourcePool),
        });

        placed++;
        fieldPlaced++;
      }
    }

    return { group: this.group, crystals: this.crystals };
  }

  clear() {
    for (const crystal of this.crystals) {
      if (crystal.mesh.parent) crystal.mesh.parent.remove(crystal.mesh);
      crystal.mesh.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    }
    this.crystals = [];
    while (this.group.children.length) this.group.remove(this.group.children[0]);
  }

  /* ── mesh builders ─────────────────────────────── */

  _createCrystalCluster(radius, seed, planetType) {
    const style = CRYSTAL_STYLES[planetType] || CRYSTAL_STYLES.rocky;
    const rng = new SeededRandom(seed);
    const group = new THREE.Group();

    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(...style.color),
      emissive: new THREE.Color().setHSL(...style.glow),
      emissiveIntensity: 0.9,
      roughness: style.roughness,
      metalness: style.metalness,
      transparent: true,
      opacity: 0.85,
    });

    // Central large shard
    const mainH = radius * rng.range(1.5, 2.5);
    const mainR = radius * rng.range(0.25, 0.4);
    const mainGeo = new THREE.ConeGeometry(mainR, mainH, rng.int(4, 6));
    const main = new THREE.Mesh(mainGeo, mat);
    main.position.y = mainH * 0.3;
    main.castShadow = true;
    group.add(main);

    // Surrounding smaller shards
    const shardCount = rng.int(4, 9);
    for (let i = 0; i < shardCount; i++) {
      const h = radius * rng.range(0.5, 1.5);
      const r = radius * rng.range(0.08, 0.2);
      const shard = new THREE.Mesh(
        new THREE.ConeGeometry(r, h, rng.int(3, 5)),
        mat,
      );
      const angle = (i / shardCount) * Math.PI * 2 + rng.range(-0.3, 0.3);
      const spread = radius * rng.range(0.3, 0.8);
      shard.position.set(
        Math.cos(angle) * spread,
        h * 0.2,
        Math.sin(angle) * spread,
      );
      // Tilt outward slightly
      shard.rotation.set(
        rng.range(-0.3, 0.3),
        angle,
        rng.range(-0.2, 0.2),
      );
      shard.castShadow = true;
      group.add(shard);
    }

    // Bounding helper mesh for cheap hit/targeting checks
    const helperGeo = new THREE.SphereGeometry(radius * 1.2, 6, 6);
    const helperMat = new THREE.MeshBasicMaterial({ visible: false });
    const helper = new THREE.Mesh(helperGeo, helperMat);
    helper.position.y = radius * 0.3;
    helper.name = 'crystal-hitbox';
    group.add(helper);

    // Shared material handle for anything that wants to tint the cluster
    group.material = mat;

    return group;
  }
}
